import React, { PureComponent } from 'react';
import styled, { keyframes } from 'styled-components';

// Shared celebratory modal shown after a student makes a role's first
// decision (Round 1 only). Reinforces that more decisions are needed
// and offers a primary CTA that jumps them to the next action.
//
// Consumers own the trigger logic (i.e. deciding it's Round 1 + no
// prior decision) — this component just renders when `open` is true
// and calls back on CTA / dismiss.

const FONT = "'Mukta Vaani', Lato, sans-serif";

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Styled = {};

Styled.Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  z-index: 3000;
  animation: ${fadeIn} 200ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.Card = styled.div`
  position: relative;
  background: #FFFFFF;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  padding: 32px 32px 28px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  font-family: ${FONT};
  color: #0F172A;
  animation: ${fadeUp} 250ms ease both;
  text-align: left;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 14px;
  background: transparent;
  border: 0;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748B;
  font-size: 22px;
  line-height: 1;

  &:hover {
    background: #F1F5F9;
    color: #0F172A;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }
`;

Styled.IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

Styled.CelebrateIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #DCFCE7;
  color: #15803D;
  font-size: 22px;
`;

Styled.Title = styled.h2`
  margin: 0;
  font-family: ${FONT};
  font-size: 20px;
  font-weight: 800;
  color: #0F172A;
`;

Styled.Body = styled.p`
  margin: 0 0 24px;
  font-size: 16px;
  line-height: 1.55;
  color: #1F2A44;
`;

Styled.ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

Styled.CtaButton = styled.button`
  padding: 12px 22px;
  border: 0;
  border-radius: 10px;
  background: #2563EB;
  color: #FFFFFF;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: #1D4ED8;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

class FirstDecisionModal extends PureComponent {
  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleKeydown);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeydown);
    }
  }

  handleKeydown = (e) => {
    if (e.key === 'Escape' && this.props.open) {
      this.handleClose();
    }
  };

  handleBackdropClick = () => {
    this.handleClose();
  };

  handleCardClick = (e) => {
    e.stopPropagation();
  };

  handleClose = () => {
    if (this.props.onClose) this.props.onClose();
  };

  handleCta = () => {
    if (this.props.onCta) this.props.onCta();
  };

  render() {
    const { open, title, body, ctaLabel } = this.props;
    if (!open) return null;
    return (
      <Styled.Backdrop
        role="dialog"
        aria-modal="true"
        onClick={this.handleBackdropClick}
      >
        <Styled.Card onClick={this.handleCardClick}>
          <Styled.CloseButton
            type="button"
            onClick={this.handleClose}
            aria-label="Close"
          >
            ×
          </Styled.CloseButton>
          {title ? (
            <Styled.IconRow>
              <Styled.CelebrateIcon aria-hidden="true">🎉</Styled.CelebrateIcon>
              <Styled.Title>{title}</Styled.Title>
            </Styled.IconRow>
          ) : (
            <Styled.IconRow>
              <Styled.CelebrateIcon aria-hidden="true">🎉</Styled.CelebrateIcon>
            </Styled.IconRow>
          )}
          <Styled.Body>{body}</Styled.Body>
          <Styled.ActionRow>
            <Styled.CtaButton type="button" onClick={this.handleCta}>
              {ctaLabel}
            </Styled.CtaButton>
          </Styled.ActionRow>
        </Styled.Card>
      </Styled.Backdrop>
    );
  }
}

export default FirstDecisionModal;



// WEBPACK FOOTER //
// ./src/components/first-decision-modal/index.js